import React, { useEffect, useState, useCallback } from "react";
import {
    CardContent,
    CardHeader,
    Grid,
    TextField,
    Button,
    ListItem,
    ListItemText,
    Alert, CardActions,
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { updateDepartment } from "../../Redux/Action";
import {Loader,FormCard, DefinitionList, IconPickerDropdown} from "../../../../../Components";
import {AutoCompleteUsers} from "../../../../Core/Users/Components/Forms/AutoCompleteUsers";
import {usePermissions} from "../../../../../Hooks/usePermissions";

export const OverviewTab = ({isLoading, setIsLoading}) => {

    const dispatch = useDispatch();
    const item = useSelector((store) => store.departments.selected);
    const {hasPermission} = usePermissions();
    const [editMode, setEditMode] = useState(false);
    const [data, setData] = useState({
        id: "",
        department_name: "",
        manager_id: "",
        icon: "",
    });
    const [errors, setErrors] = useState({});
    const [message, setMessage] = useState("");

    // Populate `data` when `item` changes
    useEffect(() => {
        if (item) {
            setData({
                id: item.id || "",
                department_name: item.department_name || "",
                manager_id: item.manager_id || "",
                icon: item.icon || "",
            });
        }
    }, [item]);

    const handleChange = useCallback(
        (e) => {
            const { name, value } = e.target;
            setData((prevData) => ({
                ...prevData,
                [name]: value,
            }));
            setErrors((prevErrors) => ({
                ...prevErrors,
                [name]: "",
            }));
        },
        [setData]
    );

    const handleSave = async () => {
        setIsLoading(true);
        await updateDepartment({dispatch, data}).then((res) => {
            if(res['success']){
                setEditMode(false);
            }
            setIsLoading(false);
            setErrors(res.errors);
            setMessage(res.message);
        });
    };

    const handleCancel = () => {
        setEditMode(false);
        setErrors({});
        setData({
            id: item.id || "",
            department_name: item.department_name || "",
            manager_id: item.manager_id || "",
            icon: item.icon || "",
        });
    };

    const renderViewMode = () => {
        return (
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <DefinitionList>
                        <ListItem>
                            <ListItemText
                                primary="Department Name"
                                secondary={item?.department_name || "Not Specified"}
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemText
                                primary="Manager"
                                secondary={item?.manager ? `${item.manager.first_name} ${item.manager.last_name}` : "Not Specified"}
                            />
                        </ListItem>
                    </DefinitionList>
                </Grid>
            </Grid>
        );
    };

    const renderEditMode = () => (
        <Grid container spacing={3}>
            {message &&
                <Grid item xs={12}>
                    <Alert severity="error">{message}</Alert>
                </Grid>
            }
            <Grid item xs={12}>
                <TextField
                    fullWidth
                    label="Name"
                    InputLabelProps={{ shrink: true }}
                    name="department_name"
                    value={data.department_name}
                    onChange={handleChange}
                    error={!!errors?.department_name}
                    helperText={errors?.department_name}
                />
            </Grid>
            <Grid item xs={12}>
                <IconPickerDropdown
                    label="Department Icon"
                    value={data.icon}
                    onChange={(icon) => setData({...data, icon})}
                    error={!!errors?.icon}
                    helperText={errors?.icon || 'Select an icon to represent this department'}
                />
            </Grid>
            <Grid item xs={12}>
                <AutoCompleteUsers 
                    field={"manager_id"} 
                    data={data} 
                    setData={setData}
                    selectedUser={item?.manager} // Pass the selected manager user object
                />
            </Grid>
        </Grid>
    );

    return (
        <Grid container spacing={3}>
            <Grid item xs={12}>
                <FormCard>
                    <CardHeader
                        title="Overview"
                        subheader="Department Subheader"
                        action={
                            !editMode && hasPermission('roster.department.update') && (
                                <Button
                                    variant="outlined"
                                    onClick={() => setEditMode(true)}
                                >
                                    Edit Details
                                </Button>
                            )
                        }
                        avatar={<Loader isLoading={isLoading} type={"linear"}/>}
                    />
                    <CardContent>
                        {editMode ? renderEditMode() : renderViewMode()}
                    </CardContent>
                    {editMode && (
                    <CardActions sx={{ p:2, justifyContent: "flex-end"}}>
                        <Button
                            variant="outlined"
                            color="secondary"
                            onClick={handleCancel}
                            disabled={isLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSave}
                            disabled={isLoading}
                        >
                            Save
                        </Button>
                    </CardActions>
                    )}
                </FormCard>
            </Grid>
        </Grid>
    );
};
